import { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import type { CoinType, CoinTypeStore } from '../stores/cointype/CoinTypeStore';
import type { GameUiStore } from '../stores/game-ui/GameUiStore';
import type { JackpotCoinType } from '../stores/jackpot/types';
import type { MetagamesWarmStore } from '../stores/metagames-warm/MetagamesWarmStore';
import { useStores } from '../stores/useStores';

/**
 * Inverse of `toJackpotCoin`: the jackpot API speaks `CC`/`SC`, the platform wallet `GC`/`SC`.
 * `CC` (Crown Coins) is the jackpot name for the `GC` bucket.
 */
export const toPlatformCoin = (coinType: JackpotCoinType): CoinType => (coinType === 'SC' ? 'SC' : 'GC');

/**
 * Launches the game the jackpot popup's `PLAY NOW` selected.
 *
 * The popup is an iframe and cannot route to a slot, so it posts `jackpotPlayGame` and
 * `GameUiStore` parks it on `pendingJackpotGame`; the router lives out here.
 *
 * Two things happen before navigating:
 *
 * 1. **The coin type is aligned to the jackpot's.** Jackpot groups are per coin, so launching a
 *    CC-group game while the lobby sits on SC would contribute the round to a jackpot the player was
 *    not shown. Switching first means the launch lands in the group whose pot they just looked at.
 * 2. **The dialog is closed after the navigation is queued**, not before — closing first shows a
 *    frame of lobby underneath, which reads as the popup having done nothing.
 *
 * `gameId` is the master game id, the same identity the lobby's own tiles link with.
 */
export function useJackpotGameLaunch(
  gameUiStore: GameUiStore,
  coinTypeStore: CoinTypeStore,
  metagamesWarmStore: MetagamesWarmStore,
) {
  const navigate = useNavigate();
  const { gameStore } = useStores();
  const pending = gameUiStore.pendingJackpotGame;

  useEffect(() => {
    if (!pending) {
      return;
    }

    const launch = gameUiStore.consumeJackpotGameLaunch();

    // The other surface's effect ran first and took it.
    if (!launch) {
      return;
    }

    const platformCoin = toPlatformCoin(launch.coinType);

    if (coinTypeStore.coinType !== platformCoin) {
      coinTypeStore.setCoinType(platformCoin);
    }

    gameStore.setLaunchSource('crown_jackpot', launch.gameId);
    navigate(`/games/${launch.gameId}`);

    if (metagamesWarmStore.activeKey === 'jackpot') {
      metagamesWarmStore.close();
    } else {
      gameUiStore.closeGameUiDialog();
    }
  }, [pending, navigate, gameUiStore, coinTypeStore, metagamesWarmStore, gameStore]);
}
